import React, { useState, useEffect, useMemo, useRef } from "react";

// Modules
import { decode } from "html-entities";
import { debounce } from "lodash";
import { useSelector } from "react-redux";

// UI components
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { FileText } from "react-feather";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Button,
  Typography,
  TextField,
  Tooltip,
  InputAdornment,
  IconButton,
  Stack,
  FormControl,
} from "@mui/material";
import BookmarkIcon from "@mui/icons-material/BookmarkBorderOutlined";
import BookmarkedIcon from "@mui/icons-material/Bookmark";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import ClearIcon from "@mui/icons-material/ClearOutlined";
import GridIcon from "@mui/icons-material/ViewModuleOutlined";
import ListIcon from "@mui/icons-material/ViewListOutlined";
import ToggleOnOutlinedIcon from "@mui/icons-material/ToggleOnOutlined";
import ToggleOffOutlinedIcon from "@mui/icons-material/ToggleOffOutlined";
import { Add } from "@mui/icons-material";

import PostFilters from "./posts/postFilters";
import NestedCategory from "./nestedCategory";

export default function PageHeader(props) {
  const [gridView, setGridView] = useState(props.gridView);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(0);

  const navigate = useNavigate();

  const entityFiltersReducer = useSelector(
    (state) => state,
  ).entityFiltersReducer;

  const entityFiltersReducerRef = useRef(entityFiltersReducer);
  const searchCallbackRef = useRef(props.searchCallback);

  const dispatch = useDispatch();

  useEffect(() => {
    searchCallbackRef.current = props.searchCallback;
  }, [props.searchCallback]);

  useEffect(() => {
    entityFiltersReducerRef.current = entityFiltersReducer;
  }, [entityFiltersReducer]);

  useEffect(() => {
    const currentFilters = entityFiltersReducerRef.current;
    const filters = {
      ...currentFilters,
      [props.searchEntityType]: {
        ...currentFilters[props.searchEntityType],
        category: selectedCategory,
      },
    };
    dispatch({ type: "UPDATE_ENTITY_FILTERS", payload: filters });
    // eslint-disable-next-line
  }, [selectedCategory]);

  const debouncedSearch = useMemo(
    () =>
      debounce((_search) => {
        const currentFilters = entityFiltersReducerRef.current;
        const filters = {
          ...currentFilters,
          [props.searchEntityType]: {
            ...currentFilters[props.searchEntityType],
            title: _search,
          },
        };
        dispatch({ type: "UPDATE_ENTITY_FILTERS", payload: filters });
        if (searchCallbackRef.current) {
          searchCallbackRef.current(_search);
        }
      }, 1000),
    // eslint-disable-next-line
    [],
  );

  useEffect(() => {
    if (props.viewToggle) {
      props.handleViewToggle(gridView);
    }
    // eslint-disable-next-line
  }, [gridView]);

  useEffect(() => {
    debouncedSearch(search);
    return () => debouncedSearch.cancel();
  }, [search, debouncedSearch]);

  const handleCategorySelection = (value) => {
    if(props.handleCategorySelection){
      props.handleCategorySelection(value);
    }
    setSelectedCategory(value);
  };

  const renderCategorySelector = () => {
    return props.categorySelector;
  };

  const renderNestedCategorySelector = () => {
    return (
      <FormControl
        variant="standard"
        fullWidth
        size="small"
        className="page-header-nested-category justify-content-end"
      >
        <NestedCategory
          owner={props.owner}
          pageHeader={true}
          categories={props.categories}
          title={props.categoryTitle}
          categoryName={props.categoryName}
          nestedMenuItemClassName={"page-header-nested-menu-item"}
          selectedCategoryItem={selectedCategory}
          handleCategorySelection={(value) => handleCategorySelection(value)}
        />
      </FormControl>
    );
  };

  const renderFavorites = () => {
    return (
      <Col xs={"auto"} className="me-1 d-none d-xxl-flex">
        <Tooltip title={"Toggle bookmarks"}>
          <Button
            className="p-0"
            color="clientPrimary"
            disableFocusRipple={true}
            disableRipple={true}
            sx={{
              "&.Mui-focusVisible": {
                outline: (theme) =>
                  `2px solid ${theme.palette.clientPrimary.main}`,
                outlineOffset: "2px",
                borderRadius: "4px",
              },
            }}
            onClick={() => {
              if (props.getFavourites) {
                props.getFavourites();
              }
            }}
            startIcon={
              <>
                {props.activeTabIndex === 1 ? (
                  <BookmarkedIcon
                    className=""
                    sx={{
                      height: "22px",
                    }}
                  />
                ) : (
                  <BookmarkIcon
                    className=""
                    sx={{
                      height: "22px",
                    }}
                  />
                )}
              </>
            }
          >
            <Typography
              owner={props.owner}
              variant="button"
              weight="regular"
              color="adminText.main"
            >
              Bookmarks
            </Typography>
          </Button>
        </Tooltip>
        {props.getArchives && (
          <Button
            className={
              props.activeTabIndex === 2 ? "filter-btn active" : "filter-btn"
            }
            onClick={() => {
              if (props.getArchives) {
                props.getArchives();
              }
            }}
          >
            <FileText />
            <span>Archive</span>
          </Button>
        )}
      </Col>
    );
  };

  const renderEditControls = () => {
    return (
      <Row className="mobile-edit-controls mx-0 d-xxl-none">
        <Col xs={12} md={6} className="mobile-edit-controls-left gx-0 d-flex mb-3 mb-md-0 pe-md-3">
          <Row className="mobile-edit-controls-left-redactor gx-0 d-flex justify-content-between w-100">
            {props.toggleSwitch && (
              <Col
                xs="auto"
                className="d-flex flex-row align-items-end justify-content-center"
              >
                <Tooltip title={"Toggle edit mode"}>
                  <Button
                    className="p-0"
                    onClick={() => navigate(props.switchLink)}
                    startIcon={
                      props.switchStatus ? (
                        <ToggleOnOutlinedIcon
                          sx={{
                            height: "24px",
                            width: "24px",
                          }}
                        />
                      ) : (
                        <ToggleOffOutlinedIcon
                          sx={{
                            height: "24px",
                            width: "24px",
                          }}
                        />
                      )
                    }
                  >
                    <Typography
                      owner={props.owner}
                      variant="button"
                      weight="regular"
                      color="adminText.main"
                    >
                      Edit mode
                    </Typography>
                  </Button>
                </Tooltip>
              </Col>
            )}
            {props.createNew && (
              <Col
                xs="auto"
                className="d-flex flex-row align-items-end justify-content-center"
              >
                <Tooltip title={`Create ${props.contentTypeName}`}>
                  <Button
                    className="p-0"
                    onClick={() => {
                      props.handleCreate();
                    }}
                    startIcon={
                      <Add
                        sx={{
                          height: "24px",
                          width: "24px",
                        }}
                      />
                    }
                  >
                    <Typography
                      owner={props.owner}
                      variant="button"
                      weight="regular"
                      color="adminText.main"
                    >
                      Create
                    </Typography>
                  </Button>
                </Tooltip>
              </Col>
            )}
            {props.enableFavorites && (
              <Col
                xs="auto"
                className="d-flex flex-row align-items-end justify-content-center"
              >
                <Tooltip title={"Toggle bookmarks"}>
                  <Button
                    className="p-0"
                    disableFocusRipple={true}
                    disableRipple={true}
                    onClick={() => {
                      if (props.getFavourites) {
                        props.getFavourites();
                      }
                    }}
                    startIcon={
                      <>
                        {props.activeTabIndex === 1 ? (
                          <BookmarkedIcon
                            className=""
                            sx={{
                              height: "22px",
                            }}
                          />
                        ) : (
                          <BookmarkIcon
                            className=""
                            sx={{
                              height: "22px",
                            }}
                          />
                        )}
                      </>
                    }
                  >
                    <Typography
                      owner={props.owner}
                      variant="button"
                      weight="regular"
                      color="adminText.main"
                    >
                      Bookmarks
                    </Typography>
                  </Button>
                </Tooltip>
                {props.getArchives && (
                  <Button
                    className={
                      props.activeTabIndex === 2
                        ? "filter-btn active"
                        : "filter-btn"
                    }
                    onClick={() => {
                      if (props.getArchives) {
                        props.getArchives();
                      }
                    }}
                  >
                    <FileText />
                    <span>Archive</span>
                  </Button>
                )}
              </Col>
            )}
          </Row>
        </Col>
        <Col xs={12} md={6} className=" gx-0 d-flex ps-md-3 mobile-edit-controls-right">
          <Row className=" gx-0 d-flex justify-content-between align-items-end w-100">
            {props.hasSearch && (
              <Col>
                <Row className="me-1">
                  <Col>{renderSearch()}</Col>
                </Row>
              </Col>
            )}
            {props.categorySelector && (
              <Col>
                <Row className={props.switchStatus ? "ms-1" : "ms-0 gx-0"}>
                  <Col>{renderCategorySelector()}</Col>
                </Row>
              </Col>
            )}
            {props.nestedCategorySelector && (
              <Col>
                <Row className={props.switchStatus ? "ms-1" : "ms-0 gx-0"}>
                  <Col>{renderNestedCategorySelector()}</Col>
                </Row>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    );
  };

  const renderSearch = () => {
    return (
      <FormControl fullWidth size="small">
        <TextField
          title="page-header-search-title"
          className="search-input"
          sub_variant="search"
          owner="client"
          variant="standard"
          autoFocus={true}
          value={search}
          placeholder="Search Title"
          label={
            <Typography
              owner={props.owner}
              variant="button"
              weight="regular"
              color="adminText.main"
            >
              Search Title
            </Typography>
          }
          onChange={(e) => {
            setSearch(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (props.searchCallback) {
                props.searchCallback(search);
              }
            }
          }}
          InputProps={{
            disableUnderline: true,
            endAdornment: (
              <>
                <InputAdornment position="start">
                  <IconButton
                    className="navbar-btn p-0"
                    color="clientPrimary"
                    onClick={() => {
                      setSearch("");
                    }}
                  >
                    {search !== "" ? (
                      <ClearIcon sx={{ fontSize: "1.2rem" }} />
                    ) : (
                      <SearchIcon sx={{ fontSize: "1.2rem" }} />
                    )}
                  </IconButton>
                </InputAdornment>
              </>
            ),
          }}
          InputLabelProps={{ style: { fontSize: 14 } }}
        />
      </FormControl>
    );
  };

  const listHeaderLabel = (item) => {
    return (
      <Col
        className={`list-header-label-column ${
          item.grow ? `flex-grow-${item.grow}` : ""
        } ${item?.className ? item.className : ""}`}
        xs={item?.width.xs ? item.width.xs : ""}
        sm={item?.width.sm ? item.width.sm : ""}
        md={item?.width.md ? item.width.md : ""}
        lg={item?.width.lg ? item.width.lg : ""}
        xl={item?.width.xl ? item.width.xl : ""}
        xxl={item?.width.xxl ? item.width.xxl : ""}
        style={item?.style ? item.style : {}}
      >
        <Typography
          owner={props.owner}
          variant={props.listHeaderLabel?.variant}
          color={props.listHeaderLabel?.color}
          weight={props.listHeaderLabel?.weight}
          lh={props.listHeaderLabel?.lh}
          className="list-header-label"
          align={item.align ? item.align : ""}
        >
          {item.title}
        </Typography>
      </Col>
    );
  };

  let pageName = props.pageName;
  try {
    pageName = decode(pageName);
  } catch (error) {
    // console.log('cannot decode')
  }

  return (
    <div
      owner={props.owner}
      // variant="screenHeader"
      className={`page-header px-0 ${props.listHeader || props.postFilters ? "mb-0" : ""}`}
    >
      <Row className="align-items-end mt-4 pt-4">
        <Col sm={12} md={12} lg={12} xl={12} xxl={8}>
          <Row className="page-name me-sm-0 mb-3 mb-xxl-0 align-items-end justify-content-between ms-0">
            <Col xs="auto" className="px-0">
              <Row className="gx-0">
                {props.pageLogo && (
                  <Col
                    xs="auto"
                    className="d-flex justify-content-center align-items-center pe-2"
                  >
                    {props.svgLogo ? (
                      <div
                        className="page-logo"
                        dangerouslySetInnerHTML={{ __html: props.pageLogo }}
                      ></div>
                    ) : (
                      <img src={props.pageLogo} alt="" className="page-logo" />
                    )}
                  </Col>
                )}
                <Col className="d-flex align-items-end">
                  <Row className="gx-0">
                    <Typography
                      className="mb-0"
                      variant="h1"
                      owner={props.owner ? props.owner : "admin"}
                      color={"adminText.dark"}
                    >
                      {pageName}
                    </Typography>
                  </Row>
                </Col>
                {props.toggleSwitch && (
                  <Col
                    xs="auto"
                    className="ms-4 d-none d-xxl-flex align-items-end"
                  >
                    <Tooltip title={"Toggle edit mode"}>
                      <Button
                        className="p-0"
                        onClick={() => navigate(props.switchLink)}
                        startIcon={
                          props.switchStatus ? (
                            <ToggleOnOutlinedIcon
                              sx={{
                                height: "24px",
                                width: "24px",
                              }}
                            />
                          ) : (
                            <ToggleOffOutlinedIcon
                              sx={{
                                height: "24px",
                                width: "24px",
                              }}
                            />
                          )
                        }
                      >
                        <Typography
                          owner={props.owner}
                          variant="button"
                          weight="regular"
                          color="adminText.main"
                        >
                          Edit mode
                        </Typography>
                      </Button>
                    </Tooltip>
                    {/* <Link
                      color="primary"
                      to={props.switchLink}
                      className="toggle-switch-control-link"
                    >
                      <FormControlLabel
                        className="toggle-switch-control-root"
                        label={
                          <Typography
                            owner={props.owner}
                            variant="button"
                            weight="regular"
                            color="adminText.main"
                          >
                            Edit Mode
                          </Typography>
                        }
                        control={
                          <Switch
                            id="edit-switch"
                            defaultChecked={props.switchStatus}
                          />
                        }
                      />
                    </Link> */}
                  </Col>
                )}
                {props.createNew && (
                  <Col
                    xs="auto"
                    className="ms-4 d-none d-xxl-flex align-items-end"
                  >
                    <Tooltip title={`Create ${props.contentTypeName}`}>
                      <Button
                        className="p-0"
                        onClick={() => {
                          props.handleCreate();
                        }}
                        startIcon={
                          <Add
                            sx={{
                              height: "24px",
                              width: "24px",
                            }}
                          />
                        }
                      >
                        <Typography
                          owner={props.owner}
                          variant="button"
                          weight="regular"
                          color="adminText.main"
                        >
                          Create
                        </Typography>
                      </Button>
                    </Tooltip>
                  </Col>
                )}
              </Row>
            </Col>
            <Col xs="auto" className="px-0">
              <Row className="align-items-center">
                <Col xs="auto" className={props.enableFavorites ? "pe-0" : ""}>
                  {props.viewToggle && (
                    <Button
                      className="d-flex p-0 align-items-center"
                      color="clientPrimary"
                      size="small"
                      disableFocusRipple={true}
                      disableRipple={true}
                      sx={{
                        "&.Mui-focusVisible": {
                          outline: (theme) =>
                            `2px solid ${theme.palette.clientPrimary.main}`,
                          outlineOffset: "2px",
                          borderRadius: "4px",
                        },
                      }}
                      onClick={() => setGridView((prevState) => !prevState)}
                      endIcon={
                        gridView ? (
                          <ListIcon
                            className="ms-1"
                            sx={{ fontSize: "1rem" }}
                          />
                        ) : (
                          <GridIcon
                            className="ms-1"
                            sx={{ fontSize: "1rem" }}
                          />
                        )
                      }
                    >
                      <Tooltip
                        title={gridView ? "List View" : "Grid View"}
                        placement="left"
                      >
                        <Typography
                          className="d-none d-sm-block"
                          owner={props.owner}
                          variant="button"
                        >
                          View as
                        </Typography>
                      </Tooltip>
                    </Button>
                  )}
                </Col>
                {props.enableFavorites && renderFavorites()}
              </Row>
            </Col>
          </Row>
        </Col>
        <Col sm={12} md={12} lg={4} className="d-none d-xxl-block">
          <Stack
            className="h-100 desktop-title-and-category-filters"
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            alignItems="stretch"
          >
            {props.hasSearch && renderSearch()}
            {props.categorySelector && renderCategorySelector()}
            {props.nestedCategorySelector && renderNestedCategorySelector()}
          </Stack>
        </Col>
      </Row>
      {renderEditControls()}

      {props.postFilters && (
        <PostFilters
          {...props.filterProps}
          selectedCategory={selectedCategory}
          search={search}
        />
      )}
      {props.listHeader && (
        <Row className="list-header-label-container pt-4 pb-0 me-0">
          {props.listLabels.length > 0 &&
            props.listLabels.map((item) => {
              return listHeaderLabel(item);
            })}
        </Row>
      )}
    </div>
  );
}
